import { partsCataloguePath, productLibraryPath } from '@constants/routes';
import type { Kit } from '@partful/shared-types';
import { useProduct } from '@providers/Product';
import { NamedLink } from '@services/partNavigation';
import { makePartUrl } from '@services/partUrl';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { makeKitUrl } from './kitUrl';

export interface KitNamedLink extends NamedLink {
  kitData: Kit;
}

export type KitsParams = {
  productId?: string;
  externalId?: string;
};

class KitClass {
  kitNamedLink: KitNamedLink[];
  kitParent: NamedLink[];
  kitPath?: Kit;
  productId?: string;

  get currentKit() {
    return this.kitPath;
  }

  constructor(
    kitPath?: Kit | undefined,
    productId?: string,
    kitNamedLink: KitNamedLink[] = [],
    kitParent: NamedLink[] = []
  ) {
    this.kitPath = kitPath;
    this.productId = productId;
    this.kitNamedLink = kitNamedLink;
    this.kitParent = kitParent;
  }
}

function getKitById(externalId: string, kits: Kit[]) {
  return kits.filter((kit) => kit.externalId === externalId);
}

export const KitContext = createContext(new KitClass(undefined));

export function KitProvider({ children }: Readonly<PropsWithChildren>) {
  const match = useRouteMatch<KitsParams>(`/${productLibraryPath}/:productId/${partsCataloguePath}/kit/:externalId?`);
  const { productId, kits, configurationName } = useProduct();

  const kitNamedLink: KitNamedLink[] = useMemo(
    () =>
      kits.map((kit) => ({
        name: kit.name,
        kitData: kit,
        url: makeKitUrl({
          productId,
          externalId: kit.externalId,
        }),
      })),
    [productId, kits]
  );

  const parentNamedLink: NamedLink[] = useMemo(() => {
    const parents: NamedLink[] = [];
    parents.push({
      name: configurationName,
      url: makePartUrl({
        productId,
        partId: '',
      }),
    });
    return parents;
  }, [productId, configurationName]);

  const externalID = match?.params.externalId && decodeURIComponent(match?.params.externalId);

  const kitPaths = externalID ? getKitById(externalID, kits) : [];

  const kitPath = kitPaths[0];

  const value = useMemo(
    () => new KitClass(kitPath, productId, kitNamedLink, parentNamedLink),
    [kitPath, productId, kitNamedLink, parentNamedLink]
  );

  return <KitContext.Provider value={value}>{children}</KitContext.Provider>;
}

export const useKit = (): KitClass => {
  const context = useContext(KitContext);
  return context;
};
